import React, { useState, useRef } from 'react';
import Navbar from "../../layout/Navbar";
import Footer from "../../layout/Footer";
import { Upload, X, CheckCircle, AlertCircle, Plus, Pen, Image as ImageIcon, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

const DeathCertificateForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenNumber, setTokenNumber] = useState('');
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState('');

  // Signature refs and state
  const sigCanvas = useRef(null);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'upload'
  const [signatureDataURL, setSignatureDataURL] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    // Application Date - Set to today's date by default
    applicationDate: getTodayDate(),

    // Deceased Information
    deceasedName: '',
    gender: '',
    age: '',
    dateOfDeath: '',
    deceasedAadhar: '',
    deceasedUhid: '',

    // Spouse Information
    maritalStatus: 'married', // married or unmarried
    spouseName: '',
    spouseAge: '',
    spouseAadhar: '',
    spouseMobile: '',

    // Father Information
    fatherName: '',
    fatherMobile: '',

    // Mother Information
    motherName: '',

    // Address Information
    houseNumber: '',
    building: '',
    street: '',
    locality: '',
    village: '',
    thana: '',
    subDistrict: '',
    district: '',
    pinCode: '',
    state: '',

    // Doctor Information
    doctorName: '',

    // File Uploads - ARRAYS for multiple files
    deceasedAadharFiles: [],
    dependentAadharFiles: []
  });

  // NEW: Handle multiple file uploads
  const handleMultipleFileChange = (e, fieldName) => {
    const files = Array.from(e.target.files);

    // Validate each file
    const validFiles = [];
    const fileErrors = [];

    files.forEach(file => {
      // Check file size (max 5MB per file)
      if (file.size > 5 * 1024 * 1024) {
        fileErrors.push(`${file.name}: File too large (max 5MB)`);
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(`${file.name}: Invalid file type`);
        return;
      }

      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors(prev => ({ ...prev, [fieldName]: fileErrors.join(', ') }));
      return;
    }

    // Add new files to existing array
    setFormData(prev => ({
      ...prev,
      [fieldName]: [...prev[fieldName], ...validFiles]
    }));

    setErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, [fieldName]: 'File size should be less than 5MB' }));
        return;
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, [fieldName]: 'Only JPG, PNG, and PDF files are allowed' }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const removeFile = (fieldName, index) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  // Signature handling functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setSignatureDataURL(null);
    setSignatureFile(null);
    setErrors(prev => ({ ...prev, signature: '' }));
  };

  const saveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      // Use toDataURL instead of getTrimmedCanvas
      const dataURL = sigCanvas.current.toDataURL('image/png');
      setSignatureDataURL(dataURL);
      setErrors(prev => ({ ...prev, signature: '' }));
    } else {
      setErrors(prev => ({ ...prev, signature: 'Please draw your signature / कृपया अपना हस्ताक्षर करें' }));
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, signature: 'File size should be less than 2MB' }));
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, signature: 'Only JPG and PNG images allowed' }));
        return;
      }

      setSignatureFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureDataURL(reader.result);
        setErrors(prev => ({ ...prev, signature: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.deceasedName.trim()) newErrors.deceasedName = 'Required / आवश्यक';
    if (!formData.gender) newErrors.gender = 'Required / आवश्यक';
    if (!formData.dateOfDeath) newErrors.dateOfDeath = 'Required / आवश्यक';
    if (formData.deceasedAadhar && formData.deceasedAadhar.trim()) {
      // Only validate format if Aadhar number is provided
      if (!/^\d{12}$/.test(formData.deceasedAadhar.trim())) {
        newErrors.deceasedAadhar = 'Invalid Aadhar Number (must be 12 digits) / अमान्य आधार नंबर (12 अंक होना चाहिए)';
      }
    }
    if (formData.maritalStatus === 'married') {
      if (!formData.spouseName.trim()) newErrors.spouseName = 'Required / आवश्यक';
      if (formData.spouseAadhar && formData.spouseAadhar.trim()) {
        // Only validate format if Spouse Aadhar number is provided
        if (!/^\d{12}$/.test(formData.spouseAadhar.trim())) {
          newErrors.spouseAadhar = 'Invalid Aadhar Number (must be 12 digits) / अमान्य आधार नंबर (12 अंक होना चाहिए)';
        }
      }
      if (!formData.spouseMobile.trim()) newErrors.spouseMobile = 'Required / आवश्यक';
      else if (!/^\d{10}$/.test(formData.spouseMobile)) newErrors.spouseMobile = 'Invalid Mobile Number / अमान्य मोबाइल नंबर';
    }

    if (!formData.fatherName.trim()) newErrors.fatherName = 'Required / आवश्यक';
    if (!formData.motherName.trim()) newErrors.motherName = 'Required / आवश्यक';

    if (!formData.houseNumber.trim()) newErrors.houseNumber = 'Required / आवश्यक';
    if (!formData.village.trim()) newErrors.village = 'Required / आवश्यक';
    if (!formData.district.trim()) newErrors.district = 'Required / आवश्यक';
    if (!formData.pinCode.trim()) newErrors.pinCode = 'Required / आवश्यक';
    else if (!/^\d{6}$/.test(formData.pinCode)) newErrors.pinCode = 'Invalid Pin Code / अमान्य पिन कोड';
    if (!formData.state.trim()) newErrors.state = 'Required / आवश्यक';

    if (!formData.doctorName.trim()) newErrors.doctorName = 'Required / आवश्यक';

    // File validation - at least 1 file required
    if (formData.deceasedAadharFiles.length === 0) {
      newErrors.deceasedAadharFiles = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
    }
    if (formData.dependentAadharFiles.length === 0) {
      newErrors.dependentAadharFiles = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
    }

    // Signature validation
    if (!signatureDataURL) {
      newErrors.signature = 'Signature is required / हस्ताक्षर आवश्यक है';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = document.querySelector('.border-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();

      // Append all non-file fields
      Object.keys(formData).forEach(key => {
        if (!key.includes('Files')) { // Skip file arrays
          if (formData[key] !== null && formData[key] !== '') {
            submitData.append(key, formData[key]);
          }
        }
      });

      // Append signature
      if (signatureMode === 'draw' && signatureDataURL) {
        // Convert base64 to blob
        const response = await fetch(signatureDataURL);
        const blob = await response.blob();
        submitData.append('applicantSignature', blob, 'signature.png');
      } else if (signatureMode === 'upload' && signatureFile) {
        submitData.append('applicantSignature', signatureFile);
      }

      // Append multiple files with same field name
      formData.deceasedAadharFiles.forEach((file, index) => {
        submitData.append('deceasedAadharFiles', file);
      });

      formData.dependentAadharFiles.forEach((file, index) => {
        submitData.append('dependentAadharFiles', file);
      });

      const response = await fetch('https://wellnessplix.in/formProxy.php?endpoint=death-certificate', {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit form');
      }

      if (result.success) {
        setTokenNumber(result.data.submissionNo);
        setPdfUrl(result.data.pdfUrl);
        setSuccess(true);
      } else {
        throw new Error(result.message || 'Submission failed');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: error.message || 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  }

  const resetForm = () => {
    setFormData({
      applicationDate: '',
      deceasedName: '',
      gender: '',
      age: '',
      dateOfDeath: '',
      deceasedAadhar: '',
      maritalStatus: 'married',
      spouseName: '',
      spouseAge: '',
      spouseAadhar: '',
      spouseMobile: '',
      fatherName: '',
      fatherMobile: '',
      motherName: '',
      houseNumber: '',
      building: '',
      street: '',
      locality: '',
      village: '',
      thana: '',
      subDistrict: '',
      district: '',
      pinCode: '',
      state: '',
      doctorName: '',
      deceasedAadharFiles: [],
      dependentAadharFiles: []
    });
    clearSignature();
    setSuccess(false);
    setTokenNumber('');
    setErrors({});
  };

  if (success) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="flex justify-center mb-6">
                <CheckCircle className="w-20 h-20 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                सफलतापूर्वक जमा किया गया!<br />
                Successfully Submitted!
              </h2>
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <p className="text-gray-600 mb-2">
                  आपका टोकन नंबर / Your Token Number
                </p>
                <p className="text-4xl font-bold text-blue-600 mb-4">
                  {tokenNumber}
                </p>
                <p className="text-sm text-gray-500">
                  यह नंबर व्हाट्सएप पर भेज दिया गया है<br />
                  This number has been sent to WhatsApp
                </p>
              </div>

            {/* PDF Download Button - NEW */}
            {pdfUrl && (
              <div className="mb-6">
                <a
                  href={`http://3.6.79.46:8000${pdfUrl}`}
                  download
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF / पीडीएफ डाउनलोड करें
                </a>
              </div>
            )}

              <div className="space-y-3">
                <button
                  onClick={resetForm}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  नया आवेदन भरें / Fill New Application
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  होम पर जाएं / Go to Home
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-2">
              मृत्यु प्रमाण पत्र बनवाने के लिए वांछित सूचनाएँ
            </h1>
            <p className="text-center text-gray-600 text-sm md:text-base">
              Information Required for Death Certificate
            </p>
            <p className="text-center text-gray-500 text-xs md:text-sm mt-2">
              (मरीज के परिजन द्वारा भरने हेतु / To be filled by patient's family)
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {/* Application Date */}
            <div className="mb-8">
              <label className="block text-gray-700 font-semibold mb-2">
                दिनांक / Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="applicationDate"
                value={formData.applicationDate}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicationDate ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {errors.applicationDate && (
                <p className="text-red-500 text-sm mt-1">{errors.applicationDate}</p>
              )}
            </div>

            {/* Deceased Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                मृतक की सूचना / Deceased Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Deceased Name */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    मृतक का नाम / Deceased Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="deceasedName"
                    value={formData.deceasedName}
                    onChange={handleInputChange}
                    placeholder="पूरा नाम दर्ज करें / Enter full name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.deceasedName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.deceasedName && (
                    <p className="text-red-500 text-sm mt-1">{errors.deceasedName}</p>
                  )}
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    लिंग / Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.gender ? 'border-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">चुनें / Select</option>
                    <option value="male">पुरुष / Male</option>
                    <option value="female">स्त्री / Female</option>
                    <option value="other">अन्य / Other</option>
                  </select>
                  {errors.gender && (
                    <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
                  )}
                </div>

                {/* Age */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    उम्र / Age
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder="उम्र / Age"
                    min="0"
                    max="150"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Date of Death */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    मृत्यु की तिथि / Date of Death <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="dateOfDeath"
                    value={formData.dateOfDeath}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.dateOfDeath ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.dateOfDeath && (
                    <p className="text-red-500 text-sm mt-1">{errors.dateOfDeath}</p>
                  )}
                </div>

                {/* Aadhar Number */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    आधार नंबर / Aadhar Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="deceasedAadhar"
                    value={formData.deceasedAadhar}
                    onChange={handleInputChange}
                    placeholder="12 अंकों का आधार नंबर / 12 digit Aadhar"
                    maxLength="12"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.deceasedAadhar ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.deceasedAadhar && (
                    <p className="text-red-500 text-sm mt-1">{errors.deceasedAadhar}</p>
                  )}
                </div>

                {/* UHID Number */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    यु.एच.आई.डी. नंबर / UHID Number
                  </label>
                  <input
                    type="text"
                    name="deceasedUhid"
                    value={formData.deceasedUhid}
                    onChange={handleInputChange}
                    placeholder=""
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.deceasedUhid ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.deceasedUhid && (
                    <p className="text-red-500 text-sm mt-1">{errors.deceasedUhid}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Spouse Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                मृतक के पति/पत्नी की सूचना / Spouse Information
              </h2>

              {/* Marital Status */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  वैवाहिक स्थिति / Marital Status <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="maritalStatus"
                      value="married"
                      checked={formData.maritalStatus === 'married'}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700">विवाहित / Married</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="maritalStatus"
                      value="unmarried"
                      checked={formData.maritalStatus === 'unmarried'}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700">अविवाहित / Unmarried</span>
                  </label>
                </div>
              </div>

              {formData.maritalStatus === 'married' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Spouse Name */}
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 font-semibold mb-2">
                      पति/पत्नी का नाम / Spouse Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="spouseName"
                      value={formData.spouseName}
                      onChange={handleInputChange}
                      placeholder="पूरा नाम दर्ज करें / Enter full name"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.spouseName ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.spouseName && (
                      <p className="text-red-500 text-sm mt-1">{errors.spouseName}</p>
                    )}
                  </div>

                  {/* Spouse Age */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      उम्र / Age
                    </label>
                    <input
                      type="number"
                      name="spouseAge"
                      value={formData.spouseAge}
                      onChange={handleInputChange}
                      placeholder="उम्र / Age"
                      min="0"
                      max="150"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Spouse Aadhar */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      आधार नंबर / Aadhar Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="spouseAadhar"
                      value={formData.spouseAadhar}
                      onChange={handleInputChange}
                      placeholder="12 अंकों का आधार नंबर / 12 digit Aadhar"
                      maxLength="12"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.spouseAadhar ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.spouseAadhar && (
                      <p className="text-red-500 text-sm mt-1">{errors.spouseAadhar}</p>
                    )}
                  </div>

                  {/* Spouse Mobile */}
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 font-semibold mb-2">
                      मोबाइल नंबर / Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="spouseMobile"
                      value={formData.spouseMobile}
                      onChange={handleInputChange}
                      placeholder="10 अंकों का मोबाइल नंबर / 10 digit mobile"
                      maxLength="10"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.spouseMobile ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.spouseMobile && (
                      <p className="text-red-500 text-sm mt-1">{errors.spouseMobile}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Father Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                मृतक के पिता की सूचना / Father's Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Father Name */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    पिता का नाम / Father's Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleInputChange}
                    placeholder="पूरा नाम दर्ज करें / Enter full name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.fatherName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.fatherName && (
                    <p className="text-red-500 text-sm mt-1">{errors.fatherName}</p>
                  )}
                </div>

                {/* Father Mobile */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    मोबाइल नंबर / Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="fatherMobile"
                    value={formData.fatherMobile}
                    onChange={handleInputChange}
                    placeholder="10 अंकों का मोबाइल नंबर / 10 digit mobile"
                    maxLength="10"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Mother Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                मृतक की माता की सूचना / Mother's Information
              </h2>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  माता का नाम / Mother's Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleInputChange}
                  placeholder="पूरा नाम दर्ज करें / Enter full name"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.motherName ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {errors.motherName && (
                  <p className="text-red-500 text-sm mt-1">{errors.motherName}</p>
                )}
              </div>
            </div>

            {/* Address Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                मृतक का पता / Deceased's Address
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* House Number */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    हाउस नंबर / House Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="houseNumber"
                    value={formData.houseNumber}
                    onChange={handleInputChange}
                    placeholder="हाउस नंबर / House Number"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.houseNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.houseNumber && (
                    <p className="text-red-500 text-sm mt-1">{errors.houseNumber}</p>
                  )}
                </div>

                {/* Building/Mohalla */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    बिल्डिंग/मोहल्ला / Building/Mohalla
                  </label>
                  <input
                    type="text"
                    name="building"
                    value={formData.building}
                    onChange={handleInputChange}
                    placeholder="बिल्डिंग/मोहल्ला / Building/Mohalla"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Street/Lane */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    स्ट्रीट/लेन का नाम / Street/Lane Name
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    placeholder="स्ट्रीट/लेन का नाम / Street/Lane Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Locality/Post Office */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    लोकैलिटी/डाकघर / Locality/Post Office
                  </label>
                  <input
                    type="text"
                    name="locality"
                    value={formData.locality}
                    onChange={handleInputChange}
                    placeholder="लोकैलिटी/डाकघर / Locality/Post Office"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Village/Town */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    गाँव/टाउन / Village/Town <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="village"
                    value={formData.village}
                    onChange={handleInputChange}
                    placeholder="गाँव/टाउन / Village/Town"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.village ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.village && (
                    <p className="text-red-500 text-sm mt-1">{errors.village}</p>
                  )}
                </div>

                {/* Thana */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    थाना / Police Station
                  </label>
                  <input
                    type="text"
                    name="thana"
                    value={formData.thana}
                    onChange={handleInputChange}
                    placeholder="थाना / Police Station"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Sub-district/Tehsil/Block */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    उपजिला/तहसील/ब्लॉक / Sub-district/Tehsil/Block
                  </label>
                  <input
                    type="text"
                    name="subDistrict"
                    value={formData.subDistrict}
                    onChange={handleInputChange}
                    placeholder="उपजिला/तहसील/ब्लॉक"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* District */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    जिला / District <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleInputChange}
                    placeholder="जिला / District"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.district ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.district && (
                    <p className="text-red-500 text-sm mt-1">{errors.district}</p>
                  )}
                </div>

                {/* Pin Code */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    पिन कोड / Pin Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="pinCode"
                    value={formData.pinCode}
                    onChange={handleInputChange}
                    placeholder="6 अंकों का पिन कोड / 6 digit pin code"
                    maxLength="6"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.pinCode ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.pinCode && (
                    <p className="text-red-500 text-sm mt-1">{errors.pinCode}</p>
                  )}
                </div>

                {/* State */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    राज्य / State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="राज्य / State"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.state ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.state && (
                    <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Doctor Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                डॉक्टर की जानकारी / Doctor's Information
              </h2>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  इलाज करने वाले डॉक्टर का नाम / Treating Doctor's Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="doctorName"
                  value={formData.doctorName}
                  onChange={handleInputChange}
                  placeholder="डॉक्टर का नाम / Doctor's Name"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.doctorName ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {errors.doctorName && (
                  <p className="text-red-500 text-sm mt-1">{errors.doctorName}</p>
                )}
              </div>
            </div>

            {/* Signature Section - NEW */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                हस्ताक्षर / Signature <span className="text-red-500">*</span>
              </h2>

              {/* Mode Selection */}
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setSignatureMode('draw');
                    clearSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${signatureMode === 'draw'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Pen className="w-5 h-5" />
                  Draw Signature / हस्ताक्षर करें
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSignatureMode('upload');
                    clearSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${signatureMode === 'upload'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <ImageIcon className="w-5 h-5" />
                  Upload Image / फ़ोटो अपलोड करें
                </button>
              </div>

              {/* Draw Mode */}
              {signatureMode === 'draw' && (
                <div>
                  <div className={`border-2 rounded-lg overflow-hidden ${errors.signature ? 'border-red-500' : 'border-gray-300'
                    }`}>
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'w-full h-48 bg-white cursor-crosshair',
                        style: { touchAction: 'none' }
                      }}
                      backgroundColor="white"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="flex-1 py-2 px-4 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Clear / साफ़ करें
                    </button>
                    <button
                      type="button"
                      onClick={saveSignature}
                      className="flex-1 py-2 px-4 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Save / सहेजें
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    👆 Use mouse or finger to draw your signature / अपना हस्ताक्षर बनाने के लिए माउस या उंगली का उपयोग करें
                  </p>
                </div>
              )}

              {/* Upload Mode */}
              {signatureMode === 'upload' && (
                <div>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${errors.signature ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                    }`}>
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          Upload signature image / हस्ताक्षर की फ़ोटो अपलोड करें
                        </span>
                        <span className="text-xs text-gray-400 mt-2">
                          JPG, PNG (Max 2MB)
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleSignatureUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Signature Preview */}
              {signatureDataURL && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700 font-semibold mb-2">
                    Signature Preview / हस्ताक्षर पूर्वावलोकन:
                  </p>
                  <div className="bg-white p-4 rounded border border-gray-300 flex justify-center">
                    <img
                      src={signatureDataURL}
                      alt="Signature"
                      className="max-h-32 object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Remove / हटाएं
                  </button>
                </div>
              )}

              {errors.signature && (
                <p className="text-red-500 text-sm mt-2">⚠️ {errors.signature}</p>
              )}
            </div>

            {/* File Upload Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                संलग्नक / Attachments
              </h2>

              {/* Deceased Aadhar Files */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  मृतक का आधार कार्ड / Deceased's Aadhar Card <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  You can upload multiple images (front, back, etc.) / आप कई छवियां अपलोड कर सकते हैं
                </p>

                {/* Display uploaded files */}
                {formData.deceasedAadharFiles.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.deceasedAadharFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                            {index + 1}
                          </span>
                          <span className="text-sm text-gray-700 truncate">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('deceasedAadharFiles', index)}
                          className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload button with Plus icon */}
                <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${errors.deceasedAadharFiles ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                  }`}>
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      {formData.deceasedAadharFiles.length === 0 ? (
                        <>
                          <Upload className="w-12 h-12 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">
                            फ़ाइल अपलोड करने के लिए क्लिक करें<br />
                            Click to upload file
                          </span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-10 h-10 text-blue-600 mb-2" />
                          <span className="text-sm text-blue-600 font-semibold">
                            और फ़ाइल जोड़ें / Add More Files
                          </span>
                        </>
                      )}
                      <span className="text-xs text-gray-400 mt-2">
                        JPG, PNG, PDF (Max 5MB each) | Multiple files allowed
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      multiple
                      onChange={(e) => handleMultipleFileChange(e, 'deceasedAadharFiles')}
                      className="hidden"
                    />
                  </label>
                </div>
                {errors.deceasedAadharFiles && (
                  <p className="text-red-500 text-sm mt-1">⚠️ {errors.deceasedAadharFiles}</p>
                )}
              </div>

              {/* Dependent Aadhar Files - Same pattern */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  आश्रित का आधार कार्ड / Dependent's Aadhar Card <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  You can upload multiple images (front, back, etc.) / आप कई छवियां अपलोड कर सकते हैं
                </p>

                {formData.dependentAadharFiles.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {formData.dependentAadharFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">
                            {index + 1}
                          </span>
                          <span className="text-sm text-gray-700 truncate">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('dependentAadharFiles', index)}
                          className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${errors.dependentAadharFiles ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                  }`}>
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      {formData.dependentAadharFiles.length === 0 ? (
                        <>
                          <Upload className="w-12 h-12 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">
                            फ़ाइल अपलोड करने के लिए क्लिक करें<br />
                            Click to upload file
                          </span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-10 h-10 text-green-600 mb-2" />
                          <span className="text-sm text-green-600 font-semibold">
                            और फ़ाइल जोड़ें / Add More Files
                          </span>
                        </>
                      )}
                      <span className="text-xs text-gray-400 mt-2">
                        JPG, PNG, PDF (Max 5MB each) | Multiple files allowed
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      multiple
                      onChange={(e) => handleMultipleFileChange(e, 'dependentAadharFiles')}
                      className="hidden"
                    />
                  </label>
                </div>
                {errors.dependentAadharFiles && (
                  <p className="text-red-500 text-sm mt-1">⚠️ {errors.dependentAadharFiles}</p>
                )}
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-4 px-6 rounded-lg font-semibold text-white transition-all ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                  }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    जमा हो रहा है... / Submitting...
                  </span>
                ) : (
                  'जमा करें / Submit'
                )}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 sm:flex-none px-6 py-4 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                रीसेट करें / Reset
              </button>
            </div>

            {/* Footer Note */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 text-center">
                <span className="font-semibold">नोट / Note:</span> सभी आवश्यक फ़ील्ड (<span className='text-red-500'>*</span>) भरना अनिवार्य है।<br />
                All required fields (<span className='text-red-500'>*</span>) must be filled.
              </p>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DeathCertificateForm;